import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  AlertTriangle,
  Bell,
  Check,
  Clock,
  Gavel,
  BookOpen,
  HelpCircle,
  Link2,
  Target,
} from 'lucide-react'
import {
  QUEUE_CODE_META,
  HIDDEN_SHARED_SIGNAL_CODES,
  renderSharedSignalLabel,
  decodeHtmlEntities,
} from './liveControlsUtils'

const QUEUE_ICON_MAP = {
  viewer_link_pending: Link2,
  close_decision_while_speaking: Gavel,
  keep_topic_open: BookOpen,
  confirm_weak_coverage: HelpCircle,
  recover_at_risk_topic: AlertTriangle,
  watch_shared_goal_risk: Target,
}

const ACCENT_CLASSES = {
  red: { badge: 'bg-red-100 text-red-700', border: 'border-red-200' },
  amber: { badge: 'bg-amber-100 text-amber-800', border: 'border-amber-200' },
  blue: { badge: 'bg-primary-100 text-primary-700', border: 'border-primary-200' },
  slate: { badge: 'bg-slate-100 text-slate-600', border: 'border-slate-200' },
}

function ActionRequiredCard({
  icon: Icon,
  codeLabel,
  accent = 'amber',
  title,
  agendaTitle,
  actions,
}) {
  const accentClasses = ACCENT_CLASSES[accent] ?? ACCENT_CLASSES.amber

  return (
    <div className={`flex items-center gap-3 rounded-xl border bg-white px-3.5 py-2.5 shadow-sm ${accentClasses.border}`}>
      <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${accentClasses.badge}`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-5 text-slate-900">{decodeHtmlEntities(title)}</p>
        {agendaTitle ? (
          <p className="text-[11px] text-slate-500 truncate">{agendaTitle}</p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 items-center gap-1.5">
          {actions}
        </div>
      ) : null}
    </div>
  )
}

const SIGNAL_ICON_MAP = {
  agenda_drift: Clock,
  shared_internal_goal_at_risk: Target,
  unanswered_question: HelpCircle,
}

export default function ActionRequiredBand({
  personalQueue = [],
  topicRows = [],
  attentionSignals = [],
  onPersonalAction,
  runAction,
  pendingByKey = {},
  readOnly = false,
}) {
  const [dismissed, setDismissed] = useState(() => new Set())

  const agendaTitleById = new Map(topicRows.map((r) => [r.agendaItemId, r.title]))

  const openQueueItems = personalQueue
    .filter((item) => item.actionState?.status === 'open')
    .filter((item) => !dismissed.has(item.itemId))

  const resolvedQueueItems = personalQueue
    .filter((item) => item.actionState?.status === 'auto_resolved')
    .filter((item) => !dismissed.has(item.itemId))

  // Filter attention signals — hide suppressed codes, locally dismissed,
  // and deduplicate shared_internal_goal_at_risk when personal queue already has watch_shared_goal_risk
  const hasPersonalGoalRisk = openQueueItems.some((item) => item.code === 'watch_shared_goal_risk')
  const visibleSignals = attentionSignals.filter(
    (s) =>
      !HIDDEN_SHARED_SIGNAL_CODES.has(s.code) &&
      !dismissed.has(s.signalId) &&
      !(s.code === 'shared_internal_goal_at_risk' && hasPersonalGoalRisk)
  )

  // Hidden when nothing actionable
  if (openQueueItems.length === 0 && resolvedQueueItems.length === 0 && visibleSignals.length === 0) return null

  const hasUrgent = openQueueItems.some(
    (item) => item.severity === 'alert' || item.code === 'recover_at_risk_topic'
  )

  return (
    <div
      className={`rounded-2xl border shadow-sm ${
        hasUrgent ? 'border-red-200 bg-red-50/60' : 'border-amber-200 bg-amber-50/50'
      }`}
    >
      <div className="flex flex-col gap-2 px-4 py-3">
        <AnimatePresence mode="popLayout">
          {openQueueItems.map((item) => {
            const codeMeta = QUEUE_CODE_META[item.code] ?? { label: 'Action', accent: 'amber' }
            const CodeIcon = QUEUE_ICON_MAP[item.code] ?? Bell
            const isPending = pendingByKey[`personal:${item.itemId}`] === true
            const agendaTitle = item.agendaItemId ? agendaTitleById.get(item.agendaItemId) : null

            return (
              <motion.div
                key={item.itemId}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                layout
              >
                <ActionRequiredCard
                  icon={CodeIcon}
                  codeLabel={codeMeta.label}
                  accent={codeMeta.accent}
                  title={item.message}
                  agendaTitle={agendaTitle}
                  actions={
                    !readOnly ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setDismissed((prev) => new Set(prev).add(item.itemId))
                            runAction(`personal:${item.itemId}`, () =>
                              onPersonalAction?.({ itemId: item.itemId, action: 'complete' })
                            )
                          }}
                          disabled={isPending}
                          className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100 transition-colors disabled:opacity-50"
                        >
                          <Check className="h-3 w-3" /> Done
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setDismissed((prev) => new Set(prev).add(item.itemId))
                            runAction(`personal:${item.itemId}`, () =>
                              onPersonalAction?.({ itemId: item.itemId, action: 'dismiss' })
                            )
                          }}
                          disabled={isPending}
                          className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-50 transition-colors disabled:opacity-50"
                        >
                          Dismiss
                        </button>
                      </>
                    ) : null
                  }
                />
              </motion.div>
            )
          })}
          {/* Auto-resolved personal queue items — shown greyed out */}
          {resolvedQueueItems.map((item) => {
            const codeMeta = QUEUE_CODE_META[item.code] ?? { label: 'Resolved', accent: 'slate' }
            const CodeIcon = QUEUE_ICON_MAP[item.code] ?? Check
            return (
              <motion.div
                key={`resolved-${item.itemId}`}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 0.6, y: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                layout
              >
                <ActionRequiredCard
                  icon={CodeIcon}
                  codeLabel={codeMeta.label}
                  accent="slate"
                  title={item.message}
                  agendaTitle={item.agendaItemId ? agendaTitleById.get(item.agendaItemId) : null}
                  actions={
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                      <Check className="h-3 w-3" /> Resolved
                    </span>
                  }
                />
              </motion.div>
            )
          })}
          {/* Attention signals — dismissible, local-only */}
          {visibleSignals.map((signal) => {
            const SignalIcon = SIGNAL_ICON_MAP[signal.code] ?? AlertTriangle
            const agendaTitle = signal.agendaItemId ? agendaTitleById.get(signal.agendaItemId) : null
            return (
              <motion.div
                key={signal.signalId}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                layout
              >
                <ActionRequiredCard
                  icon={SignalIcon}
                  codeLabel={renderSharedSignalLabel(signal) ?? signal.code}
                  accent={signal.severity === 'warning' ? 'amber' : 'slate'}
                  title={signal.message}
                  agendaTitle={agendaTitle}
                  actions={
                    <button
                      type="button"
                      onClick={() => setDismissed((prev) => new Set(prev).add(signal.signalId))}
                      className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-50 transition-colors"
                    >
                      Dismiss
                    </button>
                  }
                />
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>
    </div>
  )
}
